import { X, Upload, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { uploadImage, resolveMedia, apiError } from "../../lib/api";

export const inputCls =
  "w-full rounded-lg border border-black/15 bg-white px-3.5 py-2.5 text-sm text-nead-navy placeholder:text-nead-navy/40 outline-none focus:border-nead-pink transition-colors";

export function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-nead-navy/50">{label}</span>
      {children}
    </label>
  );
}

export function Input(props) {
  return <input {...props} className={inputCls} />;
}

export function Textarea(props) {
  return <textarea {...props} className={`${inputCls} resize-none`} />;
}

export function Select({ children, ...props }) {
  return <select {...props} className={inputCls}>{children}</select>;
}

export function Btn({ children, variant = "pink", className = "", ...props }) {
  const styles = {
    pink: "bg-nead-pink text-white hover:bg-nead-navy",
    dark: "bg-nead-navy text-white hover:bg-nead-pink",
    ghost: "bg-transparent text-nead-navy/70 hover:bg-black/5",
    outline: "border border-black/15 text-nead-navy hover:border-nead-pink hover:text-nead-pink",
    danger: "bg-red-50 text-red-600 hover:bg-red-100 border border-red-100",
    green: "bg-emerald-500 text-white hover:bg-emerald-600",
  };
  return (
    <button {...props} className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors duration-200 disabled:opacity-50 ${styles[variant]} ${className}`}>
      {children}
    </button>
  );
}

export function Modal({ open, onClose, title, children, wide = false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-10" onClick={onClose} data-testid="admin-modal">
      <div className={`w-full ${wide ? "max-w-3xl" : "max-w-lg"} rounded-2xl bg-white shadow-2xl`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-black/5 px-6 py-4">
          <h3 className="font-display font-bold text-lg text-nead-navy">{title}</h3>
          <button onClick={onClose} className="rounded-full p-1.5 text-nead-navy/50 hover:bg-black/5" data-testid="admin-modal-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

export function ImageUpload({ value, onChange, testId = "image-upload" }) {
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await uploadImage(file);
      onChange(url);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-3">
        <div className="h-16 w-24 flex-none overflow-hidden rounded-lg border border-black/10 bg-nead-mist flex items-center justify-center">
          {value ? (
            <img src={resolveMedia(value)} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="text-[10px] text-nead-navy/40">No image</span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-black/15 px-3.5 py-2 text-sm font-semibold text-nead-navy hover:border-nead-pink hover:text-nead-pink transition-colors" data-testid={`${testId}-btn`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {busy ? "Uploading…" : "Upload image"}
            <input type="file" accept="image/*" className="hidden" onChange={handle} disabled={busy} data-testid={`${testId}-input`} />
          </label>
          {value && (
            <button type="button" onClick={() => onChange("")} className="text-left text-xs text-nead-navy/50 hover:text-red-500">Remove</button>
          )}
        </div>
      </div>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="or paste an image URL" data-testid={`${testId}-url`} />
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-700",
    approved: "bg-emerald-100 text-emerald-700",
    declined: "bg-red-100 text-red-600",
    booked: "bg-sky-100 text-sky-700",
    out: "bg-nead-pink/15 text-nead-pink",
    returned: "bg-emerald-100 text-emerald-700",
  };
  return <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${map[status] || "bg-black/10 text-nead-navy/60"}`}>{status}</span>;
}
